import React from "react";
import { create } from "zustand";
import { Project } from "@/models/project";
import { ensureAllStageIds } from "@/utils/stageIdGenerator";
import type { Process } from "@/models/flowchart";

interface ChatMessage {
  id: string;
  content: string;
  sender: string;
  isNew?: boolean;
}

interface CodicentState {
  projects: Project[];
  currentProject: Project | null;
  chatMessages: ChatMessage[];

  setProjects: (projects: Project[]) => void;
  setCurrentProject: (project: Project) => void;
  updateCurrentProject: (path: string[], value: any) => void;
  updatePart: (path: string[], value: any) => void;
  saveCurrentProject: () => Promise<boolean>;
  setChatMessages: (messages: ChatMessage[]) => void;
  addChatMessage: (message: ChatMessage) => void;
  clearChatMessages: () => void;

  isChatOpen: boolean;
  setChatOpen: React.Dispatch<React.SetStateAction<boolean>>;

  previousSidebarOpen: boolean | undefined;
  setPreviousSidebarOpen: (isOpen: boolean | undefined) => void;
}

export const useCodicentStore = create<CodicentState>((set, get) => ({
  projects: [], // Not used yet. Still using projects from useReqtangleCodicent.ts
  currentProject: null,
  chatMessages: [],
  isChatOpen: false,

  setChatOpen: (value) => set({ isChatOpen: typeof value === 'function' ? value(get().isChatOpen) : value }),

  // Set list of available projects
  setProjects: (projects) => {
    localStorage.setItem("codicentProjects", JSON.stringify(projects));
    set({ projects });
  },

  // Set selected/current project
  setCurrentProject: (project) => {
    localStorage.setItem("codicentCurrentProject", JSON.stringify(project));
    set({ currentProject: project });
  },

  previousSidebarOpen: undefined,
  setPreviousSidebarOpen: (isOpen) => set({ previousSidebarOpen: isOpen }),

  // Update zustand store state
  updateCurrentProject: (path, value) => {
    const current = structuredClone(get().currentProject);
    if (!current) return;

    // Handle empty path - replace the entire project
    if (path.length === 0) {
      localStorage.setItem("codicentCurrentProject", JSON.stringify(value));
      set({ currentProject: value });
      return;
    }

    // TypeScript does not allow indexing Project with string, so we cast to any for dynamic path traversal
    let obj: any = current;
    for (let i = 0; i < path.length - 1; i++) {
      obj = obj[path[i]] ??= {};
    }
    obj[path[path.length - 1]] = value;

    localStorage.setItem("codicentCurrentProject", JSON.stringify(current));
    set({ currentProject: current });
  },

  // Update part of zustand store state - with the help of a path leading to the right part of the json tree
  updatePart: (path, value) => {
    const current = structuredClone(get().currentProject);
    if (!current) return;

    // Helper to resolve ID-based path segments like 'processes[id=1]'
    const resolveIdSelector = (obj: any, segment: string | number): any => {
      const strSegment = String(segment);
      const match = strSegment.match(/^(\w+)\[id=([^\]]+)\]$/);
      if (!match) return null;

      const [_, prop, id] = match;
      const items = obj?.[prop];
      if (!Array.isArray(items)) return null;

      return items.find((item: any) => String(item?.id) === String(id)) ?? null;
    };

    // Check if we're updating stages and ensure they all have IDs
    const isUpdatingStages = path.some(seg => String(seg).includes('stages[')) ||
      (path.includes("stages") && Array.isArray(value));

    if (isUpdatingStages && Array.isArray(value)) {
      value = value.map((stage: any, index: number) => {
        if (!stage.id || stage.id === null || stage.id === '') {
          const newId = `${Date.now()}-stage${index + 1}`;
          console.log(`Generated missing stage ID: ${newId} for stage "${stage.name || 'unnamed'}"`);
          return { ...stage, id: newId };
        }
        return stage;
      });
    }

    // Check if we're updating tasks and ensure they all have IDs
    const isUpdatingTasks = path.some(seg => String(seg).includes('tasks[')) ||
      (path.includes("tasks") && Array.isArray(value));

    if (isUpdatingTasks && Array.isArray(value)) {
      value = value.map((task: any, index: number) => {
        if (!task.id || task.id === null || task.id === '') {
          const newId = `${Date.now()}-${index + 1}`;
          console.log(`Generated missing task ID: ${newId} for task "${task.name || 'unnamed'}"`);
          return { ...task, id: newId };
        }
        return task;
      });
    }

    // Check if we're updating processes and ensure all their stages have IDs
    const isUpdatingProcesses = path.some(seg => String(seg).includes('processes[')) ||
      (path.includes("processes") && Array.isArray(value));

    if (isUpdatingProcesses && Array.isArray(value)) {
      value = value.map((process: Process) => {
        if (process && typeof process === 'object') {
          return ensureAllStageIds(process);
        }
        return process;
      });
    }

    // Handle the actual path traversal and value setting
    let obj: any = current;
    const lastIndex = path.length - 1;

    // Traverse all but the last segment
    for (let i = 0; i < lastIndex; i++) {
      const segment = path[i];
      const strSegment = String(segment);

      // Handle ID-based selectors like 'processes[id=1]'
      if (strSegment.includes('[') && strSegment.includes(']')) {
        const nextObj = resolveIdSelector(obj, segment);
        if (nextObj === null) {
          console.warn(`Could not resolve path segment: ${segment}`, { obj, path });
          return;
        }
        obj = nextObj;
      } else {
        // Handle regular path segments
        if (obj[segment] === undefined) {
          // Create nested objects as needed
          obj[segment] = {};
        }
        obj = obj[segment];
      }
    }

    // Handle the last segment (where we set the value)
    const lastSegment = path[lastIndex];
    if (typeof lastSegment === 'string' && lastSegment.includes('[') && lastSegment.includes(']')) {
      // Handle ID-based selector for the last segment
      const match = lastSegment.match(/^(\w+)\[id=([^\]]+)\]$/);
      if (match) {
        const [_, prop, id] = match;
        const items = obj?.[prop];
        if (Array.isArray(items)) {
          const index = items.findIndex((item: any) => String(item?.id) === String(id));
          if (index !== -1) {
            items[index] = value;
          } else {
            // If not found, add to the array
            items.push(value);
          }
          // Update the reference
          obj[prop] = [...items];
        }
      }
    } else {
      // Regular path assignment
      obj[lastSegment] = value;
    }

    localStorage.setItem("codicentCurrentProject", JSON.stringify(current));
    set({ currentProject: current });
  },

  // Save project from zustand store state to backend state (codicent)
  saveCurrentProject: async () => {
    const current = get().currentProject;
    if (!current) throw new Error("No current project to save");
    if (!window.Codicent) throw new Error("window.Codicent is not available");
    try {
      const json = JSON.stringify(current);
      await window.Codicent.postMessage({
        message: `@${current.codicent} #reqtangle-project-json\n${json}`,
        type: "info",
      });
      // Optionally: show a toast or log
      return true;
    } catch (error) {
      console.error("Error saving project:", error);
      throw error;
    }
  },

  setChatMessages: (messages) => {
    localStorage.setItem("codicentChatMessages", JSON.stringify(messages));
    set({ chatMessages: messages });
  },

  addChatMessage: (message) => {
    const messages = [...get().chatMessages, message];
    localStorage.setItem("codicentChatMessages", JSON.stringify(messages));
    set({ chatMessages: messages });
  },

  clearChatMessages: () => {
    localStorage.removeItem("codicentChatMessages");
    set({ chatMessages: [] });
  },
}));
